/**
 * Tiny event bus (T-007).
 *
 * Wraps `EventTarget` with a typed, named-event surface so the auth
 * layer can fan out signals (e.g. "401 from gateway with valid exp")
 * to interested UI consumers without dragging in a state-management
 * dependency. Consumers:
 *
 *   - `auth:gateway-validation-warning` — diagnostic chip in shell
 *     header (T-026), surfaced when F-300 returns 401 but the JWT is
 *     still within its `exp` (likely JWKS-reachability issue, NOT a
 *     user-side auth problem). Do NOT redirect to re-auth on this.
 *
 *   - `auth:reauth-required` — router-level handler navigates to
 *     `/onboarding/account?reauth=true`. Fired when the refresh chain
 *     fails or accounts/F-200 returns 401 after a single refresh
 *     attempt.
 *
 * Per F-310 technical spec § 6.4. Kept in `src/api/` so both API clients
 * and the auth store can import without a feature ↔ feature cycle.
 */

export type AuthEvent = "auth:gateway-validation-warning" | "auth:reauth-required";

/**
 * Listener is parameter-less by contract (auth events carry no
 * payload) — but the type allows extra arguments so callers can pass
 * a `vi.fn()` spy or a method with a wider signature without
 * compile-time complaints. Extra args are simply ignored at emit time.
 */
type Listener = (...args: unknown[]) => void;

/**
 * Singleton bus. Lives on the module so `import { eventBus } from ...`
 * always returns the same instance. Tests can reset via `clearAll()`.
 */
class EventBus {
  private listeners = new Map<AuthEvent, Set<Listener>>();

  /**
   * Subscribe to an event. Returns the unsubscribe function so
   * `useEffect` consumers can return it directly from their effect body.
   */
  on(event: AuthEvent, listener: Listener): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
    };
  }

  /** Fire all listeners for an event. Errors in a single listener don't break siblings. */
  emit(event: AuthEvent): void {
    const set = this.listeners.get(event);
    if (!set || set.size === 0) return;
    // Copy to a snapshot so listeners can unsubscribe themselves mid-emit
    // without skipping subsequent siblings.
    for (const listener of Array.from(set)) {
      try {
        listener();
      } catch (err) {
        // Don't let one bad subscriber sink the rest. Surface in dev
        // consoles via console.error so the bug is visible.
        // eslint-disable-next-line no-console
        console.error(`[eventBus] listener for "${event}" threw`, err);
      }
    }
  }

  /** Drop every subscriber. Test-only — production code never calls this. */
  clearAll(): void {
    this.listeners.clear();
  }

  /** Count subscribers for an event. Test-only helper. */
  count(event: AuthEvent): number {
    return this.listeners.get(event)?.size ?? 0;
  }
}

export const eventBus = new EventBus();
